/* ──────────────────────────────────────────────────────────────────────
   VdcPage — /vdc/ Immersive technologies (by Hordecore)
   Light hero -> Selected work CINEMA REEL (4 panels: scroll, swipe,
   arrow-key, dot nav, counter) -> Capabilities (dark navy + closing
   CTA) -> Footer.   Each panel has an "Open case" CTA leading to
   /vdc/<slug>/ detail page.   Namespace: .dz-vdc-*
   ────────────────────────────────────────────────────────────────────── */

const { useState: useVdcState, useEffect: useVdcEffect, useRef: useVdcRef } = React;

/* Assets with NO encoded -mobile variant. The 2026-05-14 batch encode covered
   apollo/golf/nursing but missed iholo, so the blind swap below requested
   vdc-iholo-reel-mobile.mp4 — a hard 404 — and the iHolo reel silently failed
   to load for every phone visitor to /vdc/ and /ar/vdc/. Serving the desktop
   file there is correct, not a regression: it is 320 KB, smaller than the
   apollo (576 KB) and golf (433 KB) mobile variants already shipped.
   Remove an entry here only once its -mobile.mp4 actually exists. */
const DZ_VDC_NO_MOBILE_VARIANT = [/vdc-iholo-reel\.mp4/i];

/* Swap auto-loading hero/reel videos to their -mobile.mp4 variants on
   viewports ≤800px. Same pattern as Hero.jsx — saves 70-94% per file. */
function dzMobileVideo(url) {
  if (typeof window === 'undefined' || !url) return url;
  if (!window.matchMedia || !window.matchMedia('(max-width: 800px)').matches) return url;
  if (DZ_VDC_NO_MOBILE_VARIANT.some((re) => re.test(url))) return url;
  return url.replace(/(\.mp4)(\?[^"'\s]*)?$/i, '-mobile$1$2');
}

function useVdcReveal(threshold) {
  const ref = useVdcRef(null);
  useVdcEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { el.classList.add('is-in-view'); io.disconnect(); }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Hero visual: "Through the headset" lens carousel ─────────────────── */
/* Round porthole that auto-cycles through real glimpses of the four
   immersive projects. Direct read on the headline "Experiences you
   step into" — you literally see what the user inside the headset sees.
   Pure React + CSS, works in every browser, no GPU configuration. */

function VdcLensSpace() {
  return (
    <svg viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice"
         className="dz-vdc-lens__space" aria-hidden="true">
      <defs>
        <radialGradient id="vdcLensSpaceGrad" cx="50%" cy="50%" r="80%">
          <stop offset="0%"  stopColor="rgba(79,200,244,0.30)" />
          <stop offset="60%" stopColor="rgba(43,61,79,1)" />
          <stop offset="100%" stopColor="rgba(26,45,62,1)" />
        </radialGradient>
      </defs>
      <rect width="800" height="800" fill="url(#vdcLensSpaceGrad)" />
      {Array.from({ length: 80 }).map((_, i) => {
        const x = (i * 71) % 800;
        const y = (i * 53) % 800;
        const r = 0.7 + (i % 4) * 0.4;
        return (
          <circle key={i} cx={x} cy={y} r={r} fill="white" opacity={0.3 + (i % 5) * 0.13}>
            <animate attributeName="opacity" values="0.1;0.85;0.1"
                     dur={`${2 + (i % 4)}s`} begin={`${(i * 0.07) % 3}s`}
                     repeatCount="indefinite" />
          </circle>
        );
      })}
      <g transform="translate(400 400)" stroke="#4FC8F4" strokeWidth="1.2" fill="none">
        <ellipse cx="0" cy="0" rx="200" ry="60"  opacity="0.32" />
        <ellipse cx="0" cy="0" rx="290" ry="90"  opacity="0.22" transform="rotate(-15)" />
        <ellipse cx="0" cy="0" rx="360" ry="120" opacity="0.16" transform="rotate(15)" />
      </g>
      <g transform="translate(400 400)">
        <circle r="78" fill="#4FC8F4" opacity="0.18" />
        <circle r="56" fill="#4FC8F4" opacity="0.85" />
      </g>
      <circle r="7" fill="#4FC8F4">
        <animateMotion dur="14s" repeatCount="indefinite"
                       path="M 600 400 a 200 60 0 1 1 -400 0 a 200 60 0 1 1 400 0" />
      </circle>
    </svg>
  );
}

function VdcLensSlide({ slide, active }) {
  const m = slide.reelMedia || slide.media || {};
  // For youtube media, the lens uses a static thumbnail (iframes are too
  // heavy for a 4s cycling carousel). Cinema reel handles the actual video.
  const ytThumb = m.type === 'youtube'
    ? (m.poster || ('https://img.youtube.com/vi/' + m.id + '/maxresdefault.jpg'))
    : null;
  return (
    <div className={'dz-vdc-lens__slide' + (active ? ' is-active' : '')}>
      {m.type === 'video' ? (
        <video src={dzMobileVideo(m.src)} muted autoPlay loop playsInline preload="metadata"
               disablePictureInPicture disableRemotePlayback
               controlsList="nodownload nofullscreen noremoteplayback"
               aria-label={m.alt || slide.title} />
      ) : m.type === 'youtube' ? (
        <img src={ytThumb} alt={m.alt || slide.title || 'VDC project preview'} loading="eager" decoding="async" />
      ) : m.type === 'image' ? (
        <img src={m.src} alt={m.alt || slide.title || 'VDC project still'} loading="eager" decoding="async" />
      ) : m.type === 'placeholder' ? (
        <VdcLensSpace />
      ) : null}
      <div className="dz-vdc-lens__shade" aria-hidden="true" />
    </div>
  );
}

// Pull a short, single-line caption for the lens overlay.
// Priority: title's first segment if reasonably short, otherwise derive
// a clean project name from the slug ("al-arabi" → "Al Arabi").
function vdcLensCaption(slide) {
  if (!slide) return '';
  const titleFirst = (slide.title || '').split(/[—.]/)[0].trim();
  if (titleFirst && titleFirst.length > 0 && titleFirst.length <= 24) {
    return titleFirst;
  }
  return (slide.slug || '')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function VdcLens() {
  const data = (typeof window !== 'undefined' && window.DZ_VDC && window.DZ_VDC.projects) || null;
  const slides = (data && data.items) || [];
  const total = slides.length;
  const [active, setActive] = useVdcState(0);
  const [progress, setProgress] = useVdcState(0);

  useVdcEffect(() => {
    if (total === 0) return;
    const PERIOD = 4000; // ms per slide
    let raf = 0;
    let start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const pct = Math.min(1, elapsed / PERIOD);
      setProgress(pct);
      if (elapsed >= PERIOD) {
        setActive((i) => (i + 1) % total);
        start = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  if (total === 0) {
    return <div className="dz-vdc-lens dz-vdc-lens--empty" aria-hidden="true" />;
  }
  const current = slides[active];
  const caption = vdcLensCaption(current);
  const tags = (current.tags || []).slice(0, 2);

  // Stroke-dasharray for the progress arc — circumference of r=14 ≈ 87.96
  const ARC = 88;
  const dash = (ARC * progress).toFixed(1);

  return (
    <div className="dz-vdc-lens" role="img"
         aria-label={`Live preview — ${caption} (${active + 1} of ${total})`}>
      <div className="dz-vdc-lens__frame">
        <div className="dz-vdc-lens__stage">
          {slides.map((s, i) => (
            <VdcLensSlide key={s.slug} slide={s} active={i === active} />
          ))}
        </div>
        <div className="dz-vdc-lens__scan"     aria-hidden="true" />
        <div className="dz-vdc-lens__vignette" aria-hidden="true" />

        {/* Top overlay: LIVE pill + circular progress arc */}
        <div className="dz-vdc-lens__top" aria-hidden="true">
          <span className="dz-vdc-lens__live">
            <span className="dz-vdc-lens__live-dot" />
            <span className="dz-vdc-lens__live-text">Live preview</span>
          </span>
          <svg className="dz-vdc-lens__arc" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none"
                    stroke="rgba(255,255,255,0.18)" strokeWidth="1.6" />
            <circle cx="18" cy="18" r="14" fill="none"
                    stroke="#4FC8F4" strokeWidth="1.6" strokeLinecap="round"
                    strokeDasharray={`${dash} ${ARC}`}
                    transform="rotate(-90 18 18)" />
          </svg>
        </div>

        {/* Bottom overlay: counter + project name + tag chips */}
        <div className="dz-vdc-lens__bottom" aria-hidden="true">
          <span className="dz-vdc-lens__counter">
            {String(active + 1).padStart(2, '0')} <span className="dz-vdc-lens__counter-of">/</span> {String(total).padStart(2, '0')}
          </span>
          <span className="dz-vdc-lens__title">{caption}</span>
          {tags.length > 0 && (
            <span className="dz-vdc-lens__tags">
              {tags.map((t) => (
                <span key={t} className="dz-vdc-lens__tag">{t}</span>
              ))}
            </span>
          )}
        </div>

        {/* Click-anywhere advance — invisible button covering the lens */}
        <button
          type="button"
          className="dz-vdc-lens__hit"
          aria-label="Show next preview"
          onClick={() => setActive((i) => (i + 1) % total)}
        />
      </div>
    </div>
  );
}

/* ── HERO (light) ─────────────────────────────────────────────────────── */
function VdcHero({ data }) {
  const ref = useVdcReveal(0.05);
  return (
    <section className="dz-vdc-hero" ref={ref}>
      <div className="dz-vdc-hero__inner">
        <div className="dz-vdc-hero__copy">
          <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
          <h1 className="dz-vdc-hero__title">
            <span className="dz-vdc-hero__title-lead">{data.title.lead}</span>
            <span className="dz-vdc-hero__title-accent">{data.title.accent}</span>
          </h1>
          <p className="dz-vdc-hero__sub">{data.sub}</p>
          {data.ctas && (
            <div className="dz-vdc-hero__cta">
              <a className="dz-btn dz-btn--aqua" href={data.ctas.primary.href}>{data.ctas.primary.label}</a>
              {data.ctas.secondary && <a className="dz-btn dz-vdc-btn--outline" href={data.ctas.secondary.href}>{data.ctas.secondary.label}</a>}
            </div>
          )}
          {data.stats && (
            <ul className="dz-vdc-hero__stats">
              {data.stats.map((s, i) => (
                <li key={i} style={{ '--vdc-i': i }}>
                  <span className="dz-vdc-hero__stat-num">{s.value}{s.suffix}</span>
                  <span className="dz-vdc-hero__stat-lbl">{s.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dz-vdc-hero__visual">
          <VdcLens />
        </div>
      </div>
    </section>
  );
}

/* ── Placeholder — stylized SVG for projects without final media ───── */
function VdcPlaceholder({ symbol }) {
  if (symbol === 'space') {
    return (
      <svg className="dz-vdc-placeholder" viewBox="0 0 800 480" aria-hidden="true">
        <defs>
          <radialGradient id="vdcSpaceGrad" cx="50%" cy="50%" r="80%">
            <stop offset="0%"  stopColor="rgba(79,200,244,0.22)" />
            <stop offset="60%" stopColor="rgba(43,61,79,1)" />
            <stop offset="100%" stopColor="rgba(26,45,62,1)" />
          </radialGradient>
        </defs>
        <rect width="800" height="480" fill="url(#vdcSpaceGrad)" />
        {Array.from({ length: 60 }).map((_, i) => {
          const x = (i * 53) % 800;
          const y = (i * 37) % 480;
          const r = 0.6 + (i % 4) * 0.4;
          return (
            <circle key={i} cx={x} cy={y} r={r} fill="white" opacity={0.3 + (i % 5) * 0.13}>
              <animate attributeName="opacity" values="0.1;0.85;0.1" dur={`${2 + (i % 4)}s`}
                       begin={`${(i * 0.07) % 3}s`} repeatCount="indefinite" />
            </circle>
          );
        })}
        <g transform="translate(400 240)" stroke="currentColor" strokeWidth="1" fill="none">
          <ellipse cx="0" cy="0" rx="180" ry="50" opacity="0.32" />
          <ellipse cx="0" cy="0" rx="260" ry="80" opacity="0.22" transform="rotate(-15)" />
          <ellipse cx="0" cy="0" rx="320" ry="110" opacity="0.16" transform="rotate(15)" />
        </g>
        <g transform="translate(400 240)">
          <circle r="70" fill="currentColor" opacity="0.18" />
          <circle r="50" fill="currentColor" opacity="0.85" />
          <ellipse cx="0" cy="0" rx="120" ry="14" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.65"
                   transform="rotate(-12)" />
        </g>
        <circle r="6" fill="currentColor">
          <animateMotion dur="14s" repeatCount="indefinite"
                         path="M 580 240 a 180 50 0 1 1 -360 0 a 180 50 0 1 1 360 0" />
        </circle>
      </svg>
    );
  }
  return null;
}

/* ── Reel panel media — image / video / youtube / SVG placeholder ────── */
function VdcReelMedia({ media }) {
  if (!media) return null;
  if (media.type === 'video') {
    return (
      <video
        src={dzMobileVideo(media.src)}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload nofullscreen noremoteplayback"
        aria-label={media.alt}
      />
    );
  }
  if (media.type === 'youtube') {
    // Chromeless autoplay-muted-loop iframe.
    // Loop on YouTube requires loop=1 AND playlist=VIDEO_ID (single-video loop quirk).
    const params =
      'autoplay=1&mute=1&loop=1&playlist=' + media.id +
      '&controls=0&modestbranding=1&rel=0&playsinline=1' +
      '&disablekb=1&iv_load_policy=3&cc_load_policy=0&fs=0';
    return (
      <iframe
        className="dz-vdc-reel__yt"
        src={'https://www.youtube-nocookie.com/embed/' + media.id + '?' + params}
        title={media.alt || 'Project preview'}
        allow="autoplay; encrypted-media; picture-in-picture"
        frameBorder="0"
        loading="lazy"
        tabIndex="-1"
      />
    );
  }
  if (media.type === 'image') {
    return <img src={media.src} alt={media.alt || ''} loading="lazy" decoding="async" />;
  }
  if (media.type === 'placeholder') {
    return <VdcPlaceholder symbol={media.symbol} />;
  }
  return null;
}

/* ── A single reel panel ─────────────────────────────────────────────── */
function VdcReelPanel({ item, idx }) {
  return (
    <article className="dz-vdc-reel__panel" data-i={idx} aria-roledescription="slide">
      <div className="dz-vdc-reel__media">
        <VdcReelMedia media={item.reelMedia || item.media} />
      </div>
      <div className="dz-vdc-reel__shade" aria-hidden="true" />

      <div className="dz-vdc-reel__copy">
        <div className="dz-vdc-reel__copy-main">
          {item.index && <div className="dz-vdc-reel__index">{item.index}</div>}
          {item.kicker && <div className="dz-vdc-reel__kicker">{item.kicker}</div>}
          <h3 className="dz-vdc-reel__title">{item.title}</h3>
          {(item.tagline || item.sub) && <p className="dz-vdc-reel__tagline">{item.tagline || item.sub}</p>}
        </div>

        <div className="dz-vdc-reel__copy-side">
          <ul className="dz-vdc-reel__tags">
            {(Array.isArray(item.tags) ? item.tags : item.tag ? [item.tag] : []).map((t) => (<li key={t}>{t}</li>))}
          </ul>
          {(item.caseLink || item.href) ? (
            <a className="dz-vdc-reel__cta" href={item.caseLink || item.href}
               aria-label={'Open case — ' + item.title}>
              <span>Open case</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          ) : (
            <span className="dz-vdc-reel__cta dz-vdc-reel__cta--soon" aria-label="Case page in production">
              <span>In production</span>
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/* ── PROJECTS — Cinema Reel (scroll-snap horizontal carousel) ────────── */
function VdcReel({ data }) {
  const ref      = useVdcReveal(0.05);
  const reelRef  = useVdcRef(null);
  const [active, setActive] = useVdcState(0);
  const total = data.items.length;

  /* IntersectionObserver inside the reel — track which panel is centered */
  useVdcEffect(() => {
    const reel = reelRef.current;
    if (!reel) return;
    const panels = reel.querySelectorAll('.dz-vdc-reel__panel');
    if (!panels.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.6) {
          const i = parseInt(e.target.dataset.i, 10);
          if (!Number.isNaN(i)) setActive(i);
        }
      });
    }, { root: reel, threshold: [0, 0.6, 1] });
    panels.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, []);

  /* Keyboard nav — arrow keys scroll the reel */
  useVdcEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goTo(active + 1);
      if (e.key === 'ArrowLeft')  goTo(active - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function goTo(i) {
    const reel = reelRef.current;
    if (!reel) return;
    const target = Math.max(0, Math.min(total - 1, i));
    const panel = reel.querySelectorAll('.dz-vdc-reel__panel')[target];
    if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  return (
    <section className="dz-vdc-projects" ref={ref}>
      <div className="dz-vdc-projects__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2">{data.title}</h2>
        <p className="dz-vdc-lead">{data.lead}</p>
      </div>

      <div className="dz-vdc-reel" ref={reelRef}>
        {data.items.map((item, i) => (
          <VdcReelPanel item={item} idx={i} key={item.slug} />
        ))}
      </div>

      <div className="dz-vdc-reel__controls">
        <div className="dz-vdc-reel__counter" aria-live="polite">
          <strong>{pad(active + 1)}</strong>
          <span className="dz-vdc-reel__counter-sep"> · </span>
          <span className="dz-vdc-reel__counter-total">{pad(total)}</span>
        </div>

        <div className="dz-vdc-reel__dots" role="tablist" aria-label="Selected work">
          {data.items.map((_, i) => (
            <button key={i}
                    role="tab"
                    aria-selected={i === active}
                    aria-label={'Go to project ' + (i + 1)}
                    className={'dz-vdc-reel__dot' + (i === active ? ' is-active' : '')}
                    onClick={() => goTo(i)} />
          ))}
        </div>

        <div className="dz-vdc-reel__arrows">
          <button className="dz-vdc-reel__arrow"
                  aria-label="Previous project"
                  disabled={active === 0}
                  onClick={() => goTo(active - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="dz-vdc-reel__arrow"
                  aria-label="Next project"
                  disabled={active === total - 1}
                  onClick={() => goTo(active + 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Capability glyphs ───────────────────────────────────────────────── */
function VdcCapGlyph({ name }) {
  const c = { width: '40', height: '40', viewBox: '0 0 64 64', fill: 'none',
              stroke: 'currentColor', strokeWidth: '1.75',
              strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'vr': return (
      <svg {...c}>
        <rect x="6" y="18" width="52" height="28" rx="8" />
        <circle cx="20" cy="32" r="6" opacity="0.7"><animate attributeName="r" values="5;7;5" dur="2.4s" repeatCount="indefinite" /></circle>
        <circle cx="44" cy="32" r="6" opacity="0.7"><animate attributeName="r" values="5;7;5" dur="2.4s" begin="0.4s" repeatCount="indefinite" /></circle>
        <line x1="14" y1="18" x2="20" y2="12" /><line x1="50" y1="18" x2="44" y2="12" />
      </svg>
    );
    case 'ar': return (
      <svg {...c}>
        <path d="M32 8 L52 18 L52 46 L32 56 L12 46 L12 18 Z" opacity="0.55" />
        <path d="M32 8 L52 18 L32 28 L12 18 Z" />
        <path d="M32 28 L32 56" opacity="0.45" strokeDasharray="2 3" />
        <circle cx="32" cy="28" r="3" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'mr': return (
      <svg {...c}>
        <circle cx="22" cy="32" r="14" />
        <circle cx="42" cy="32" r="14" opacity="0.7" />
        <path d="M28 32 a14 14 0 0 0 8 0 a14 14 0 0 0 -8 0 Z" fill="currentColor" stroke="none" opacity="0.45">
          <animate attributeName="opacity" values="0.3;0.65;0.3" dur="2.4s" repeatCount="indefinite" />
        </path>
      </svg>
    );
    case 'hologram': return (
      <svg {...c}>
        <ellipse cx="32" cy="50" rx="20" ry="5" />
        <path d="M16 50 L32 14 L48 50" />
        <path d="M22 38 L42 38" opacity="0.6" />
        <path d="M26 30 L38 30" opacity="0.45" />
        <circle cx="32" cy="14" r="3" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <ellipse cx="32" cy="58" rx="24" ry="3" stroke="none" fill="currentColor" opacity="0.25">
          <animate attributeName="opacity" values="0.15;0.4;0.15" dur="2s" repeatCount="indefinite" />
        </ellipse>
      </svg>
    );
    case 'training': return (
      <svg {...c}>
        <circle cx="32" cy="32" r="22" />
        <circle cx="32" cy="32" r="14" opacity="0.55" />
        <circle cx="32" cy="32" r="6"  opacity="0.85" />
        <line x1="32" y1="6"  x2="32" y2="14" />
        <line x1="32" y1="50" x2="32" y2="58" />
        <line x1="6"  y1="32" x2="14" y2="32" />
        <line x1="50" y1="32" x2="58" y2="32" />
        <circle cx="32" cy="32" r="2" fill="currentColor" stroke="none">
          <animate attributeName="r" values="2;3.5;2" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    case 'education': return (
      <svg {...c}>
        <path d="M6 24 L32 12 L58 24 L32 36 Z" />
        <path d="M16 30 V44 a16 16 0 0 0 32 0 V30" opacity="0.65" />
        <line x1="56" y1="24" x2="56" y2="40" />
        <circle cx="56" cy="42" r="2" fill="currentColor" stroke="none" />
      </svg>
    );
    case 'retail': return (
      <svg {...c}>
        <path d="M10 18 L14 10 L50 10 L54 18 L54 50 L10 50 Z" />
        <line x1="10" y1="18" x2="54" y2="18" />
        <path d="M22 26 a 10 8 0 0 0 20 0" opacity="0.55" />
        <rect x="20" y="36" width="24" height="10" fill="currentColor" stroke="none" opacity="0.18">
          <animate attributeName="opacity" values="0.18;0.45;0.18" dur="2.4s" repeatCount="indefinite" />
        </rect>
      </svg>
    );
    case 'custom': return (
      <svg {...c}>
        <circle cx="32" cy="32" r="10" />
        <path d="M32 6  L32 14"  /><path d="M32 50 L32 58"  />
        <path d="M6 32 L14 32"  /><path d="M50 32 L58 32"  />
        <path d="M14 14 L20 20" /><path d="M44 44 L50 50" opacity="0.7" />
        <path d="M50 14 L44 20" /><path d="M20 44 L14 50" opacity="0.7" />
        <circle cx="32" cy="32" r="3" fill="currentColor" stroke="none">
          <animate attributeName="opacity" values="1;0.5;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </svg>
    );
    default: return <svg {...c}><circle cx="32" cy="32" r="20" /></svg>;
  }
}

/* ── CAPABILITIES — tile grid + closing CTA (dark navy) ──────────────── */
function VdcCapabilities({ data }) {
  const ref = useVdcReveal(0.12);
  return (
    <>
      <section className="dz-vdc-cap" ref={ref}>
        <div className="dz-vdc-cap__head">
          <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
          <h2 className="dz-vdc-h2 dz-vdc-h2--on-dark">{data.title}</h2>
          <p className="dz-vdc-lead dz-vdc-lead--on-dark">{data.lead}</p>
        </div>
        <div className="dz-vdc-cap__grid">
          {data.tiles.map((t, i) => (
            <article className="dz-vdc-ctile" key={i} style={{ '--vdc-i': i }}>
              <div className="dz-vdc-ctile__glyph"><VdcCapGlyph name={t.glyph} /></div>
              <h3 className="dz-vdc-ctile__label">{t.label}</h3>
              <p className="dz-vdc-ctile__text">{t.text}</p>
            </article>
          ))}
        </div>
      </section>

      {data.cta && (
        <section className="dz-vdc-closing">
          <div className="dz-vdc-closing__inner">
            <p className="dz-vdc-closing__line">{data.cta.line}</p>
            <a className="dz-vdc-closing__btn" href={data.cta.href}>
              {data.cta.label}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </section>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   SEARCH-INTENT PROSE SECTIONS (commercial content expansion, B1)
   Additive only — each reuses the page's existing .dz-vdc-* anatomy
   (light .dz-vdc-projects head + .dz-vdc-lead prose; dark .dz-vdc-cap
   grid; global .dz-section .dz-faq accordion). No new styling.
   ══════════════════════════════════════════════════════════════════════ */

/* Light prose section — eyebrow + h2 + paragraphs (+ optional sources).
   Same shell as the Projects head; .dz-vdc-lead styles each paragraph. */
function VdcProse({ data }) {
  const ref = useVdcReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-vdc-projects" ref={ref}>
      <div className="dz-vdc-projects__head" style={{ textAlign: 'start', alignItems: 'flex-start', maxWidth: 880 }}>
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2">{data.title}</h2>
        {data.lead && <p className="dz-vdc-lead">{data.lead}</p>}
        {data.paragraphs && data.paragraphs.map((p, i) => (
          <p className="dz-vdc-lead" key={i} style={{ marginTop: i === 0 ? 0 : 14 }}>{p}</p>
        ))}
        {data.link && (
          <a className="dz-vdc-reel__cta" href={data.link.href} style={{ marginTop: 18 }}>
            <span>{data.link.label}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
        {data.sources && (
          <ul className="dz-vdc-lead" style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', fontSize: 13 }}>
            {data.sources.map((s, i) => (
              <li key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>
                <a href={s.href} target="_blank" rel="noopener" style={{ color: 'inherit' }}>{s.label} ↗</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* BIM vs VDC — definitional table (reuses global .dz-tmq-compare anatomy). */
function VdcCompare({ data }) {
  const ref = useVdcReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-tmq-compare" ref={ref}>
      <div className="dz-tmq-compare__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2">{data.title}</h2>
        <p className="dz-vdc-lead">{data.lead}</p>
      </div>
      <div className="dz-tmq-compare__scroll" role="region" aria-label={data.tableAriaLabel} tabIndex={0}>
        <table className="dz-tmq-compare__table">
          <thead>
            <tr>
              <th scope="col" aria-label={data.firstColLabel}></th>
              {data.cols.map((c, i) => (
                <th scope="col" key={i} className={i === 1 ? 'dz-tmq-compare__hero' : undefined}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i}>
                <th scope="row">{r.label}</th>
                {r.cells.map((cell, j) => (
                  <td key={j} className={j === 1 ? 'dz-tmq-compare__hero' : undefined}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(data.note || data.link) && (
        <div className="dz-tmq-compare__head" style={{ marginTop: 28 }}>
          {data.note && <p className="dz-vdc-lead">{data.note}</p>}
          {data.link && (
            <a className="dz-vdc-reel__cta" href={data.link.href} style={{ marginTop: 14 }}>
              <span>{data.link.label}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )}
        </div>
      )}
    </section>
  );
}

/* Why / When — label + text point list (reuses .dz-vdc-cap dark grid +
   .dz-vdc-ctile cards, text-only). */
function VdcPoints({ data }) {
  const ref = useVdcReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-vdc-cap" ref={ref}>
      <div className="dz-vdc-cap__head">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2 dz-vdc-h2--on-dark">{data.title}</h2>
        {data.lead && <p className="dz-vdc-lead dz-vdc-lead--on-dark">{data.lead}</p>}
      </div>
      <div className="dz-vdc-cap__grid">
        {data.points.map((p, i) => (
          <article className="dz-vdc-ctile" key={i} style={{ '--vdc-i': i }}>
            <h3 className="dz-vdc-ctile__label">{p.label}</h3>
            <p className="dz-vdc-ctile__text">{p.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* Methods / Benefits — glyph tile grid (identical to Capabilities grid). */
function VdcTileGrid({ data }) {
  const ref = useVdcReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-vdc-cap" ref={ref}>
      <div className="dz-vdc-cap__head">
        <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2 dz-vdc-h2--on-dark">{data.title}</h2>
        {data.lead && <p className="dz-vdc-lead dz-vdc-lead--on-dark">{data.lead}</p>}
        {data.ctaLink && <p className="dz-vdc-cta-link">{data.ctaLink.text} <a href={data.ctaLink.href}>{data.ctaLink.label} {document.documentElement.lang === 'ar' ? '←' : '→'}</a></p>}
      </div>
      <div className="dz-vdc-cap__grid">
        {data.tiles.map((t, i) => (
          <article className="dz-vdc-ctile" key={i} style={{ '--vdc-i': i }}>
            <div className="dz-vdc-ctile__glyph"><VdcCapGlyph name={t.glyph} /></div>
            <h3 className="dz-vdc-ctile__label">{t.label}</h3>
            <p className="dz-vdc-ctile__text">{t.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* Workflow — numbered delivery spine (reuses global .dz-tmq-howbuilt). */
function VdcWorkflow({ data }) {
  const ref = useVdcReveal(0.12);
  if (!data) return null;
  return (
    <section className="dz-tmq-howbuilt" ref={ref}>
      <div className="dz-tmq-howbuilt__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-vdc-h2">{data.title}</h2>
        <p className="dz-vdc-lead">{data.lead}</p>
      </div>
      <ol className="dz-tmq-howbuilt__steps">
        {data.steps.map((s, i) => (
          <li className="dz-tmq-howbuilt__step" key={i} style={{ '--tmq-i': i }}>
            <span className="dz-tmq-howbuilt__step-num" aria-hidden="true">{i + 1}</span>
            <h3 className="dz-tmq-howbuilt__step-label">{s.label}</h3>
            <p className="dz-tmq-howbuilt__step-text">{s.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* FAQ — exact home/TwinMaq accordion (all Q&A in the DOM → crawlable). */
function VdcFaq({ data }) {
  const ref = useVdcReveal(0.08);
  if (!data) return null;
  return (
    <section className="dz-section dz-faq dz-tmq-faqsec" ref={ref} aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <div className="dz-faq__list" id="dz-vdc-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              <div className="dz-faq-item__body">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Page root ────────────────────────────────────────────────────────── */
function VdcPage() {
  const data = window.DZ_VDC;
  if (!data) {
    return (
      <main className="dz-vdc dz-vdc-missing">
        <h1>VDC · Immersive technologies</h1>
        <p>Content layer failed to load.</p>
      </main>
    );
  }
  return (
    <main className="dz-vdc" id="dz-main">
      <VdcHero          data={data.hero} />
      <VdcProse         data={data.whatVdc} />
      <VdcTileGrid      data={data.methods} />
      <VdcPoints        data={data.whyWhen} />
      <VdcCompare       data={data.bimVsVdc} />
      <div id="vdc-work"><VdcReel data={data.projects} /></div>
      <VdcWorkflow      data={data.workflow} />
      <VdcCapabilities  data={data.capabilities} />
      <VdcProse         data={data.vision2030} />
      <VdcTileGrid      data={data.benefits} />
      <VdcFaq           data={data.faq} />
    </main>
  );
}

window.VdcPage = VdcPage;
